import {
    HttpErrorResponse,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable, of, Subject, throwError } from "rxjs";
import { catchError, finalize, map, switchMap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { LoaderService } from "src/app/core/services/ui/loader.service";
import { NotificationsService } from "src/app/core/services/notifications/notifications.service";
import { ResourceService } from "src/app/core/services/api/resource.service";
import {
    INJECT_ACCESS_TOKEN,
    IS_MEASUREMENT_REQUEST,
    MEASUREMENT_REQUEST_URL,
    SKIP_ERROR_TOAST,
} from "./http-context-tokens";
import { v4 as uuidv4 } from "uuid";

//cache url-assignments to avoid requests
const INFLUX_URLS_ASSIGNMENTS_MAP_MAX_CACHE_SIZE = 5242880; //5mb
let INFLUX_URLS_ASSIGNMENTS_MAP: Record<string, string[]> = {};
const REQUEST_ID_HEADER = "X-Request-Id";

@Injectable()
export class HttpRequestsInterceptor implements HttpInterceptor {
    private _resourceService: ResourceService = inject(ResourceService);
    private _loaderService: LoaderService = inject(LoaderService);
    private _notificationsService: NotificationsService =
        inject(NotificationsService);
    private readonly _apiOrigin = environment.API_ORIGIN;
    private readonly _apiMeasurementsOrigin =
        environment.API_MEASUREMENTS_ORIGIN;
    private readonly _authOrigin = environment.AUTH_ORIGIN;
    private readonly unsubscribe$ = new Subject();

    intercept(req: HttpRequest<unknown>, next: HttpHandler) {
        req = this.withRequestId(req);
        let url = req.url;
        let fullUrlReq: HttpRequest<unknown> = req.clone();
        if (
            fullUrlReq.url.includes("/analytics/") ||
            fullUrlReq.url.includes("/analytics-new/") ||
            fullUrlReq.url.includes("/trips_noon/")
        ) {
            fullUrlReq = req.clone({
                headers: req.headers.set(
                    "Authorization",
                    `Bearer ${this.getToken()}`,
                ),
                url: url,
            });
        } else if (req.context.get(IS_MEASUREMENT_REQUEST)) {
            let obs: Observable<string>;
            // Local use only - useLocalMeasurementsOverride to point to local measurements API
            // const useLocalOverride = (
            //     environment as { useLocalMeasurementsOverride?: boolean }
            // ).useLocalMeasurementsOverride;
            // if (useLocalOverride && this._apiMeasurementsOrigin) {
            //     obs = of(this._apiMeasurementsOrigin);
            // } else {
            const existingUrl = req.context.get(MEASUREMENT_REQUEST_URL).trim();
            if (existingUrl) {
                obs = of(existingUrl);
            } else {
                const assignmentToken = req.params.get("assignmentToken");
                if (
                    !assignmentToken ||
                    assignmentToken === "undefined" ||
                    assignmentToken === "null"
                ) {
                    return this.traceRequest(req, next.handle(req));
                }

                if (
                    JSON.stringify(INFLUX_URLS_ASSIGNMENTS_MAP).length >=
                    INFLUX_URLS_ASSIGNMENTS_MAP_MAX_CACHE_SIZE
                ) {
                    INFLUX_URLS_ASSIGNMENTS_MAP = {};
                }

                let influxApiUrl = Object.entries(
                    INFLUX_URLS_ASSIGNMENTS_MAP,
                ).find(([_, tokens]) => tokens.includes(assignmentToken))?.[0];

                obs = !influxApiUrl
                    ? this._resourceService
                          .getByAssignmentToken(assignmentToken)
                          .pipe(
                              map((response) => {
                                  const influxApiUrl =
                                      response.client.influxApiUrl!;
                                  if (
                                      Array.isArray(
                                          INFLUX_URLS_ASSIGNMENTS_MAP[
                                              influxApiUrl
                                          ],
                                      )
                                  ) {
                                      INFLUX_URLS_ASSIGNMENTS_MAP[
                                          influxApiUrl
                                      ].push(assignmentToken);
                                  } else {
                                      INFLUX_URLS_ASSIGNMENTS_MAP[
                                          influxApiUrl
                                      ] = [assignmentToken];
                                  }
                                  return influxApiUrl;
                              }),
                          )
                    : of(influxApiUrl);
            }
            // }

            return obs.pipe(
                switchMap((influxApiUrl) => {
                    const cloneReq = this.withRequestId(
                        req.clone({
                            headers: req.headers.set(
                                "Authorization",
                                `Bearer ${this.getToken()}`,
                            ),
                            url: `${influxApiUrl}${req.url}`,
                        }),
                    );

                    return this.traceRequest(cloneReq, next.handle(cloneReq));
                }),
            );
        } else if (req.context.get(INJECT_ACCESS_TOKEN)) {
            fullUrlReq = req.clone({
                headers: req.headers.set(
                    "Authorization",
                    `Bearer ${this.getToken()}`,
                ),
            });
        } else if (
            !fullUrlReq.url.includes("http") &&
            fullUrlReq.url.includes("auth")
        ) {
            fullUrlReq = req.clone({
                headers: req.headers.set(
                    "Authorization",
                    `Bearer ${this.getToken()}`,
                ),
                url: this._authOrigin + url,
            });
        } else if (
            fullUrlReq.url.includes("http") &&
            fullUrlReq.url.includes("data")
        ) {
            fullUrlReq = req.clone({
                headers: req.headers
                    .set("Authorization", `Bearer ${this.getToken()}`)
                    .set("Web", "true"),
                withCredentials: true,
                url: `${req.url}`,
            });
        } else if (
            !fullUrlReq.url.includes("http") &&
            !fullUrlReq.url.startsWith("/assets") &&
            !fullUrlReq.url.startsWith("assets") &&
            !fullUrlReq.url.startsWith("./assets") &&
            !fullUrlReq.url.includes("auth")
        ) {
            if (url.startsWith("/placeholder")) {
                url = url.replace("/placeholder", "");
            }

            fullUrlReq = req.clone({
                headers: req.headers
                    .set("Authorization", `Bearer ${this.getToken()}`)
                    .set("Web", "true"),
                withCredentials: true,
                url: `${this._apiOrigin}${req.url}`,
            });
        }

        const toRun = next.handle(fullUrlReq);

        this._loaderService.show();
        return this.traceRequest(
            fullUrlReq,
            toRun.pipe(
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 498) {
                        return this._refreshToken(next, fullUrlReq);
                    }
                    if (!req.context.get(SKIP_ERROR_TOAST) && this.getToken()) {
                        this._notificationsService.displayHttpError(
                            error.error,
                        );
                    }
                    return throwError(() => error);
                }),
                finalize(() => {
                    this._loaderService.hide();
                }),
            ),
        );
    }

    private withRequestId(req: HttpRequest<unknown>): HttpRequest<unknown> {
        if (req.headers.has(REQUEST_ID_HEADER)) {
            return req;
        }

        return req.clone({
            headers: req.headers.set(REQUEST_ID_HEADER, this.createRequestId()),
        });
    }

    private createRequestId(): string {
        if (typeof crypto?.randomUUID === "function") {
            return crypto.randomUUID();
        }
        return uuidv4();
    }

    private traceRequest<T>(
        req: HttpRequest<unknown>,
        source: Observable<T>,
    ): Observable<T> {
        const requestId = req.headers.get(REQUEST_ID_HEADER) ?? "unknown";
        const started = performance.now();

        return source.pipe(
            finalize(() => {
                if (!environment.production) {
                    const durationMs = Math.round(performance.now() - started);
                    console.debug(
                        `[HTTP] ${req.method} ${req.urlWithParams} ${durationMs}ms (${requestId})`,
                    );
                }
            }),
        );
    }

    private _refreshToken(next: HttpHandler, req: HttpRequest<unknown>) {
        const refreshReq = this.withRequestId(
            req.clone({
                url: this._authOrigin + "/api/user/refresh-token/",
                headers: req.headers
                    .set("Authorization", `Bearer ${this.getRefreshToken()}`)
                    .set("Web", "true"),
                method: "POST",
                body: {},
            }),
        );

        this.traceRequest(refreshReq, next.handle(refreshReq)).subscribe({
            next: (token: any) => {
                const accessToken =
                    token?.access_token ?? token?.body?.access_token;
                if (accessToken) {
                    localStorage.setItem("access_token", accessToken);
                    sessionStorage.setItem("access_token", accessToken);
                    const refreshToken =
                        token?.refresh_token ?? token?.body?.refresh_token;
                    if (refreshToken) {
                        localStorage.setItem("refresh_token", refreshToken);
                        sessionStorage.setItem("refresh_token", refreshToken);
                    }
                    this.unsubscribe$.next(true);
                }
            },
            error: () => {
                localStorage.removeItem("access_token");
                localStorage.removeItem("refresh_token");
                sessionStorage.removeItem("access_token");
                sessionStorage.removeItem("refresh_token");
                sessionStorage.removeItem("vessels-filters");
                location.href = "/login";
            },
        });

        return this.unsubscribe$.pipe(
            switchMap(() => {
                const newReq = this.withRequestId(
                    req.clone({
                        headers: req.headers.set(
                            "Authorization",
                            `Bearer ${this.getToken()}`,
                        ),
                    }),
                );
                return this.traceRequest(newReq, next.handle(newReq));
            }),
        );
    }

    getToken() {
        return (
            localStorage.getItem("access_token") ||
            sessionStorage.getItem("access_token")
        );
    }

    getRefreshToken() {
        return (
            localStorage.getItem("refresh_token") ||
            sessionStorage.getItem("refresh_token")
        );
    }
}
